// Library
import {
	DetailedHTMLProps,
	SelectHTMLAttributes,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from 'react';
import { Text } from '@components';
import { WarningQld } from '@public';
import Select, { StylesConfig } from 'react-select';

import { FieldWidth } from '..';

// CSS Style for this module
import styles from './selectField.module.scss';

type Option = { value: string; label: string };
type AccessibilityProps = Pick<
	DetailedHTMLProps<SelectHTMLAttributes<HTMLSelectElement>, HTMLSelectElement>,
	'id' | 'name' | 'required'
>;

interface SelectFieldProps {
	/** Custom styling for the container */
	customStyles?: string;
	/**
	 * Variant of the select field
	 */
	variant?: 'outline' | 'filled';
	/** Select options of the dropdown list  */
	options?: Option[];
	/** Defaul option of the dropdown list  */
	defaultValue?: string;
	/** Default text that is displayed within the field */
	placeholder?: string;
	/**
	 * Helper text for additional guidance about how the input field will be used.
	 * It should only take up a single line
	 */
	hintText?: string;
	/**
	 * Helper text wrapped by HTML elements for additional guidance about how the input field will be used.
	 */
	hintChildren?: React.ReactNode;
	/** Msg to be displayed when the status is error */
	errorMsg?: string;
	/**
	 * onChange function for keeping track field value
	 * debounce is built in
	 */
	handleChange?: (value: string) => void;
	/**
	 * Is the select in a state of loading (async)
	 */
	isLoading?: boolean;
	/**
	 * Text to display when loading options
	 */
	loadingText?: string;
	/**
	 * Boolean for determining whether this is a disabled field
	 */
	disabled?: boolean;
	/**
	 * Width size of the input element.
	 * Accepts 'small', 'medium', 'large' or 'full' as fluid width.
	 * Or 'fixedWidth1' to 'fixedWidth6' for a standardized fixed width.
	 */
	size?: FieldWidth;
	/**
	 * Additional props to be passed to the underlying input element for accessibility purposes.
	 * @see https://reactjs.org/docs/accessibility.html#programmatic-access-to-event-handlers
	 */
	accessibilityProps?: AccessibilityProps;
	/**
	 * Boolean for determining whether this field has to be autofocused
	 */
	autoFocus?: boolean;
	/** Element identifier selector for test */
	testId?: string;
}

/**
 * A dropdown list
 * @param {SelectFieldProps} Prop
 * @returns  A dropdown list
 */
export const SelectField = ({
	customStyles,
	variant = 'outline',
	options = [],
	defaultValue,
	placeholder = 'Please select',
	hintText,
	hintChildren,
	errorMsg,
	disabled,
	autoFocus,
	isLoading,
	loadingText,
	size = 'full',
	handleChange = () => {
		return;
	},
	accessibilityProps,
	testId,
}: SelectFieldProps) => {
	const selectInputRef = useRef<any>(null); // eslint-disable-line @typescript-eslint/no-explicit-any
	const [selectedOption, setSelectedOption] = useState<Option | undefined>(initSelectedOption());
	const hasError = useMemo(() => Boolean(errorMsg), [errorMsg]);
	const internalId = useId();
	const [hintId] = useState(`${internalId}-hint`);
	const [errorId] = useState(`${internalId}-error`);

	function initSelectedOption() {
		if (defaultValue) {
			let defaultOption: Option | undefined = options?.find(
				option => option.value === defaultValue,
			);
			if (defaultOption === undefined) {
				defaultOption = { value: defaultValue, label: defaultValue };
			}
			return defaultOption;
		}
	}

	const handleSelect = (option: Option) => {
		setSelectedOption(option);
		handleChange(option?.value);
	};

	/**
	 * Clear internal value when external value gets empty
	 */
	useEffect(() => {
		if (!defaultValue || defaultValue.length == 0) {
			selectInputRef.current.clearValue(); // clear the value of react-select component using the ref
		}
	}, [defaultValue]);

	const selectStyles: StylesConfig = {
		control: (styles, { isFocused }) => ({
			...styles,
			outlineOffset: '2px',
			outline: isFocused ? '3px solid var(--QLD-color-light__focus)' : 'none',
		}),
	};

	return (
		<div className={[styles.container, customStyles].join(' ')}>
			<div className={styles.textContainer}>
				{hintText && (
					<div>
						<Text variant="muted" id={hintId}>
							{hintText}
						</Text>
						{hintChildren && (
							<div className={styles.hint} id={`${hintId}-additional`}>
								{hintChildren}
							</div>
						)}
					</div>
				)}
				{hasError && (
					<div className={styles.errorContainer}>
						<Text variant="error" id={errorId} role="alert" className={styles.errorContent}>
							<WarningQld customStyles={styles.errorIcon} /> {errorMsg}
						</Text>
					</div>
				)}
			</div>
			<Select
				ref={selectInputRef}
				classNamePrefix="select"
				styles={selectStyles}
				className={[
					styles.select,
					styles[variant],
					styles[`field-width--${size}`],
					styles.classToCatchWidthOnly,
					hasError ? styles.error : '',
				].join(' ')}
				options={options}
				defaultValue={selectedOption}
				onChange={(newValue: unknown) => {
					if (newValue) {
						handleSelect(newValue as Option);
					}
				}}
				placeholder={placeholder}
				isDisabled={disabled}
				isLoading={isLoading}
				loadingMessage={() => loadingText}
				theme={theme => ({
					...theme,
					colors: {
						...theme.colors,
						primary: '#dcf6ff',
						primary50: '#dcf6ff',
					},
				})}
				// Props for accessibility
				inputId={accessibilityProps?.id}
				name={accessibilityProps?.name}
				required={accessibilityProps?.required}
				autoFocus={autoFocus}
				// Testing
				data-testid={testId}
			/>
		</div>
	);
};
